import React from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import {
  AssetStatus,
  AssetCondition,
  AssetCategoryView,
} from "../../../../api/General/assets";

interface AssetFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  categoryId: number | undefined;
  onCategoryChange: (val: number | undefined) => void;
  status: AssetStatus | undefined;
  onStatusChange: (val: AssetStatus | undefined) => void;
  condition: AssetCondition | undefined;
  onConditionChange: (val: AssetCondition | undefined) => void;
  categories: AssetCategoryView[];
  onReset: () => void;
}

const AssetFilters: React.FC<AssetFiltersProps> = ({
  search,
  onSearchChange,
  categoryId,
  onCategoryChange,
  status,
  onStatusChange,
  condition,
  onConditionChange,
  categories,
  onReset,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[200px]">
        <svg
          className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          placeholder={t("assets.searchPlaceholder")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full ps-10 pe-11 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="asset-search"
        />
        <button
          type="button"
          onClick={onReset}
          title={t("assets.resetFilters")}
          aria-label={t("assets.resetFilters")}
          className="absolute end-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          data-testid="asset-filters-reset"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <select
        value={categoryId ?? ""}
        onChange={(e) => onCategoryChange(e.target.value ? Number(e.target.value) : undefined)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-category"
      >
        <option value="">{t("assets.allCategories")}</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.name}
          </option>
        ))}
      </select>

      <select
        value={status !== undefined ? String(status) : ""}
        onChange={(e) =>
          onStatusChange(e.target.value !== "" ? (e.target.value as AssetStatus) : undefined)
        }
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-status"
      >
        <option value="">{t("assets.allStatuses")}</option>
        <option value={AssetStatus.Available}>{t("assets.statusAvailable")}</option>
        <option value={AssetStatus.Issued}>{t("assets.statusIssued")}</option>
        <option value={AssetStatus.UnderMaintenance}>{t("assets.statusUnderMaintenance")}</option>
        <option value={AssetStatus.Retired}>{t("assets.statusRetired")}</option>
      </select>

      <select
        value={condition !== undefined ? String(condition) : ""}
        onChange={(e) =>
          onConditionChange(
            e.target.value !== "" ? (e.target.value as AssetCondition) : undefined
          )
        }
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-condition"
      >
        <option value="">{t("assets.allConditions")}</option>
        <option value={AssetCondition.Excellent}>{t("assets.conditionExcellent")}</option>
        <option value={AssetCondition.Good}>{t("assets.conditionGood")}</option>
        <option value={AssetCondition.Fair}>{t("assets.conditionFair")}</option>
        <option value={AssetCondition.Poor}>{t("assets.conditionPoor")}</option>
      </select>
    </div>
  );
};

export default AssetFilters;
